import type { DragMoveEvent } from '@dnd-kit/core'
import clamp from 'lodash/fp/clamp'
import type React from 'react'
import { useCallback, useRef } from 'react'
import type { VirtuosoHandle } from 'react-virtuoso'
import { fromEvent } from 'rxjs'

/* eslint-disable-next-line import/no-restricted-paths -- FIXME: UXP-4500 */
import useDebouncedCallback from '@src/lib/hooks/useDebouncedCallback'
/* eslint-disable-next-line import/no-restricted-paths -- FIXME: UXP-4500 */
import useObservable from '@src/lib/hooks/useObservable'

const trackSpacingX = 2
const trackSpacingY = 3
const trackWidth = 7
const debouncedShowScrollToBottomButtonOptions = {
  leading: false,
  trailing: true,
  maxWait: 200,
}
/** Number of pixels the user needs to scroll before the scroll to bottom button should appear */
const showScrollToBottomButtonThreshold = 360

export interface ScrollerControllerOptions {
  knobBackground: string
  knobHoverBackground: string

  knobRef: React.MutableRefObject<HTMLDivElement | null>
  onPassiveScroll?: (left: number, top: number) => void
  atBottomStateChange?: (atBottom: boolean) => void
  initialTopMostItemIndex?: number
  scrollbar: boolean

  scrollerRef: React.MutableRefObject<HTMLElement | null>

  scrollToBottomButtonRef: React.MutableRefObject<HTMLDivElement | null>

  virtuosoHandle: React.MutableRefObject<VirtuosoHandle | null>
}

export interface ScrollerController extends ScrollerControllerOptions {
  dndListeners: {
    onDragMove: (event: DragMoveEvent) => void
    onDragStart: (event: DragMoveEvent) => void
    onDragEnd: (event: DragMoveEvent) => void
  }
  viewportListeners: {
    onMouseEnter: () => void
    onMouseLeave: () => void
  }
  virtuosoProps: {
    atBottomStateChange?: (atBottom: boolean) => void
    totalListHeightChanged?: (height: number) => void
  }
  handleViewportHeightChanged: (height: number) => void
  trackSpacingX: number
  trackSpacingY: number
  trackWidth: number
}

interface ScrollerControllerState {
  hovered: boolean
  knobOffset: number
  knobOffsetOriginal: number | null
  scrollLeft: number
  scrollTop: number
  totalListHeight: number
  viewportHeight: number
  atBottom: boolean | null
}

export default function useScrollerController(
  options: ScrollerControllerOptions,
): ScrollerController {
  const {
    knobHoverBackground,
    knobRef,
    onPassiveScroll,
    scrollToBottomButtonRef,
    scrollbar,
    scrollerRef,
    virtuosoHandle,
  } = options

  const { current: state } = useRef<ScrollerControllerState>({
    hovered: false,
    knobOffset: 0,
    knobOffsetOriginal: null,
    scrollLeft: 0,
    scrollTop: 0,
    totalListHeight: 0,
    viewportHeight: 0,
    atBottom: null,
  })

  const calculateKnobHeight = () => {
    const { totalListHeight, viewportHeight } = state

    return viewportHeight >= totalListHeight
      ? 0
      : Math.max(
          60,
          (viewportHeight / totalListHeight) * viewportHeight - trackSpacingY * 2,
        )
  }

  const calculateAvailableKnobScrollHeight = () => {
    const { viewportHeight } = state
    const knobHeight = calculateKnobHeight()
    const trackHeight = viewportHeight - trackSpacingY * 2

    return trackHeight - knobHeight
  }

  const updateKnobEl = () => {
    const knobEl = knobRef.current

    if (!knobEl) {
      return
    }

    const { hovered, knobOffset, knobOffsetOriginal } = state

    const dragging = knobOffsetOriginal !== null
    const knobHeight = calculateKnobHeight()

    requestAnimationFrame(() => {
      if (dragging) {
        knobEl.style.backgroundColor = knobHoverBackground
      } else {
        knobEl.style.backgroundColor = ''
      }

      knobEl.style.opacity = `${hovered || dragging ? 1 : 0}`
      knobEl.style.height = `${knobHeight}px`
      knobEl.style.top = `${knobOffset}px`
    })
  }

  const showScrollToBottomButton = useCallback(() => {
    const scrollToBottomEl = scrollToBottomButtonRef.current

    if (scrollToBottomEl) {
      // eslint-disable-next-line react-compiler/react-compiler -- UXP-3732 - Fix React Compiler errors
      scrollToBottomEl.style.display = 'block'
    }
  }, [scrollToBottomButtonRef])

  const debouncedShowScrollToBottomButton = useDebouncedCallback(
    showScrollToBottomButton,
    200,
    debouncedShowScrollToBottomButtonOptions,
  )

  const hideScrollToBottomButton = () => {
    const scrollToBottomEl = scrollToBottomButtonRef.current

    debouncedShowScrollToBottomButton.cancel()

    if (scrollToBottomEl) {
      scrollToBottomEl.style.display = 'none'
    }
  }

  const updateScrollToBottomEl = () => {
    requestAnimationFrame(() => {
      const { scrollTop, totalListHeight, viewportHeight } = state

      if (
        totalListHeight - viewportHeight - scrollTop >
        showScrollToBottomButtonThreshold
      ) {
        debouncedShowScrollToBottomButton()
      } else {
        hideScrollToBottomButton()
      }
    })
  }

  const onMouseEnter = () => {
    state.hovered = true
    updateKnobEl()
  }

  const onMouseLeave = () => {
    state.hovered = false
    updateKnobEl()
  }

  const onDragStart = () => {
    state.knobOffsetOriginal = state.knobOffset
    updateKnobEl()
  }

  const onDragEnd = () => {
    state.knobOffsetOriginal = null
    updateKnobEl()
  }

  const onDragMove = (event: DragMoveEvent) => {
    const { knobOffsetOriginal, totalListHeight, viewportHeight } = state

    if (knobOffsetOriginal === null) {
      return
    }

    const availableScrollHeight = totalListHeight - viewportHeight
    const availableKnobScrollHeight = calculateAvailableKnobScrollHeight()
    const clampTop = clamp(0, availableKnobScrollHeight)
    const top = clampTop(knobOffsetOriginal + event.delta.y)

    if (state.knobOffset !== top) {
      state.knobOffset = top
      updateKnobEl()
      virtuosoHandle.current?.scrollTo({
        top: (top / availableKnobScrollHeight) * availableScrollHeight,
      })
    }
  }

  const atBottomStateChange = (atBottom: boolean) => {
    state.atBottom = atBottom
    options.atBottomStateChange?.(atBottom)
  }

  const totalListHeightChanged = (height: number) => {
    if (scrollbar) {
      state.totalListHeight = height
      onScroll(state.scrollLeft, state.scrollTop)
    }
  }

  const handleViewportHeightChanged = (height: number) => {
    state.viewportHeight = Math.ceil(height)
    onScroll(state.scrollLeft, state.scrollTop)
  }

  const onScroll = (left: number, top: number) => {
    const { totalListHeight, viewportHeight } = state
    const availableScrollHeight = totalListHeight - viewportHeight
    const availableKnobScrollHeight = calculateAvailableKnobScrollHeight()

    state.knobOffset = (top / availableScrollHeight) * availableKnobScrollHeight
    state.scrollLeft = left
    state.scrollTop = top

    updateKnobEl()
    updateScrollToBottomEl()
  }

  useObservable(
    () => {
      if (!scrollerRef.current || !scrollbar) {
        return
      }
      return fromEvent(scrollerRef.current, 'scroll', { passive: true })
    },
    () => {
      if (!scrollerRef.current || !scrollbar) {
        return
      }
      const { scrollLeft, scrollTop } = scrollerRef.current
      onScroll(scrollLeft, scrollTop)
      onPassiveScroll?.(scrollLeft, scrollTop)
    },
    [scrollbar],
  )

  return {
    ...options,
    dndListeners: {
      onDragMove,
      onDragStart,
      onDragEnd,
    },
    viewportListeners: {
      onMouseEnter,
      onMouseLeave,
    },
    virtuosoProps: {
      atBottomStateChange,
      totalListHeightChanged,
    },
    handleViewportHeightChanged,
    trackSpacingX,
    trackSpacingY,
    trackWidth,
  }
}
